import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeSlash, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import logo from "@/assets/logo_with_name.png";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const inputCls = "w-full px-3 py-2.5 text-sm rounded-xl border border-slate-200 bg-slate-50 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-400/20 transition";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    if (!token) { setError("Invalid reset link — please request a new one"); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Reset failed");
      }
      setDone(true);
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-white rounded-3xl shadow-xl border border-slate-100 p-8 text-center space-y-4">
          <WarningCircle size={48} className="mx-auto text-red-400" weight="duotone" />
          <h2 className="text-lg font-bold text-slate-800">Invalid reset link</h2>
          <p className="text-sm text-slate-500">This link is missing a token. Please request a new password reset from the login page.</p>
          <button onClick={() => navigate("/login")} className="w-full bg-amber-400 hover:bg-amber-500 text-white font-semibold rounded-xl py-2.5 text-sm transition-colors">
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm page-enter">
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/60 border border-slate-100 p-8">
          <div className="text-center mb-6">
            <div className="w-48 overflow-hidden mx-auto">
              <img src={logo} alt="HostFolio" className="w-full object-contain" style={{ marginTop: "-28%", marginBottom: "-22%" }} />
            </div>
          </div>

          {done ? (
            <div className="text-center space-y-4">
              <CheckCircle size={52} className="mx-auto text-green-500" weight="duotone" />
              <h2 className="text-lg font-bold text-slate-800">Password updated!</h2>
              <p className="text-sm text-slate-500">Sign in using your existing account email and this HostFolio password. Your listings stay in the same account, and Google sign-in still works.</p>
              <button
                onClick={() => navigate("/login")}
                className="w-full bg-amber-400 hover:bg-amber-500 text-white font-semibold rounded-xl py-2.5 text-sm transition-colors"
              >
                Sign In
              </button>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-bold text-slate-800 mb-1">Set new password</h2>
              <p className="text-sm text-slate-600 mb-5">Choose a separate password for HostFolio. This does not change your Google password.</p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    placeholder="New password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className={inputCls + " pr-10"}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <input
                  type={showPw ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  className={inputCls}
                  autoComplete="new-password"
                />
                {error && (
                  <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
                    <WarningCircle size={14} weight="fill" />
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl py-2.5 text-sm transition-colors shadow-sm"
                >
                  {loading ? "Updating…" : "Update Password"}
                </button>
              </form>

              <button onClick={() => navigate("/login")} className="w-full mt-3 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                Back to Login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
